// @ts-check
import cieloEngine from '../../../engines/cielo';
import { maskCardNumber } from '../../../utils/cardNumberMask';

/**
 * @typedef {import('./CardStrategy').CardStrategy} CardStrategy
 * @typedef {import('./CardStrategy').CardInfo} CardInfo
 */

/**
 * Cielo card strategy supporting both SOP and backend tokenization.
 * @function CieloCardStrategy
 * @returns {CardStrategy}
 */
const CieloStrategy = () => {
  /**
   * Create payload for Cielo.
   * Supports both SOP (client-side) and backend tokenization flows.
   * @param {CardInfo} cardInfo - The card information.
   */
  const createPayload = async (
    cardInfo,
    { threeDSValidationData, enableThreeDS, enableSilentOrderPost, skipThreeDS, isThreeDSRetry },
  ) => {
    const {
      cardNumber,
      holderName,
      expirationMonth,
      expirationYear,
      cardBrand,
      cvv2,
      paymentToken,
    } = cardInfo;

    let token;
    let payload;

    // If paymentToken exists, use SOP flow (client-side tokenization)
    if (paymentToken && enableSilentOrderPost) {
      // SOP already tokenized the card, use the PaymentToken as cielo_token
      token = paymentToken;
      payload = {
        card_number: maskCardNumber(cardNumber),
        card_holder_name: holderName,
        card_brand: cardBrand,
        card_exp_month: expirationMonth,
        card_exp_year: expirationYear,
        credit_card_tokens: {
          cielo_token: token,
        },
      };
    } else {
      // Legacy flow: backend tokenization
      token = await cieloEngine.tokenizeCard({
        holderName,
        cardNumber,
        expirationMonth,
        expirationYear,
        cardBrand,
        cvv: cvv2,
      });
      payload = {
        card_number: maskCardNumber(cardNumber),
        card_holder_name: holderName,
        card_brand: cardBrand,
        card_exp_month: expirationMonth,
        card_exp_year: expirationYear,
        credit_card_tokens: {
          cielo_token: token,
        },
      };
    }

    if (enableThreeDS && threeDSValidationData && !skipThreeDS) {
      const { cavv, xid, eci, version, referenceId } = threeDSValidationData;
      payload = {
        ...payload,
        meta: {
          cavv,
          xid,
          eci,
          version,
          reference_id: referenceId,
        },
      };
    }

    if (isThreeDSRetry) {
      payload = {
        ...payload,
        three_ds_retry: true,
      };
    }

    return payload;
  };

  return { createPayload };
};

const cieloStrategy = CieloStrategy();

export default cieloStrategy;
